import { apiClient } from './api';

export interface PaginationParams {
  page?: number;
  per_page?: number;
}

export interface ListResponse<T> {
  items: T[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

/**
 * Base service class providing common CRUD operations for API resources
 * @template T - The resource type
 * @template TCreate - The creation payload type
 * @template TUpdate - The update payload type  
 * @template TFilters - The filter parameters type
 */
export abstract class BaseService<T, TCreate, TUpdate, TFilters = {}> {
  protected abstract baseUrl: string;

  protected buildQueryParams(filters: TFilters & PaginationParams): URLSearchParams {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });
    
    return params;
  }

  async create(data: TCreate): Promise<T> {
    const response = await apiClient.post(this.baseUrl, data);
    return response.data;
  }

  async getList(filters: TFilters & PaginationParams = {} as TFilters & PaginationParams): Promise<ListResponse<T>> {
    const params = this.buildQueryParams(filters);
    const response = await apiClient.get(`${this.baseUrl}?${params.toString()}`);
    return response.data;
  }

  async getById(id: string): Promise<T> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data;
  }

  async update(id: string, data: TUpdate): Promise<T> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    return response.data;
  }

  async delete(id: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  async downloadPDF(id: string): Promise<Blob> {
    const response = await apiClient.get(`${this.baseUrl}/${id}/pdf`, {
      responseType: 'blob'
    });
    return response.data;
  }
}